export type Aside = {
  id: string;
  position: 'right' | 'left';
};

/**
 * Checks if an HTML element matches one of the specified valid tags.
 *
 * @param element - The HTML element to check.
 * @param validTags - An array of valid tag names.
 */
export const isValidTag = (
  element: HTMLElement,
  validTags: string[],
): boolean => {
  return validTags.map(tag => tag.toUpperCase()).includes(element.tagName);
};

/**
 * Calculates the total height of a figure element, including its caption.
 *
 * The height is computed based on the figure's dataset attributes (width, height, percent)
 * and the maximum content width of the article. It also adds the height of any caption elements.
 *
 * @param figure - The figure element to calculate the height from.
 * @returns The total computed height of the figure in pixels.
 */
export const getFigureTotalHeight = (figure: HTMLElement): number => {
  const width = Number(figure.dataset.width);
  const height = Number(figure.dataset.height);
  const percent = Number(figure.dataset.percent);
  if (!width || !height || !percent) return 0;

  // Get max content article width by retrieving max-width from the CSS variable (--content-max-width)
  const articleText =
    document.body.querySelector<HTMLElement>('.bo-article-text');
  if (!articleText) return 0;

  const maxWidth = parseInt(
    getComputedStyle(articleText).getPropertyValue('--content-max-width'),
  );

  // Calculate the actual image height based on the maxWidth and aspect ratio
  const actualHeight = maxWidth * (height / width) * (percent / 100);

  // Add caption height if present
  const captionElements = figure.lastElementChild?.children;
  if (!captionElements) return actualHeight;

  // Sum the heights of all caption elements
  const computedHeight = Array.from(captionElements).reduce(
    (accHeight, child) => {
      if (child instanceof HTMLElement) {
        return accHeight + child.clientHeight;
      }
      return accHeight;
    },
    actualHeight, // Start with the image height
  );

  return computedHeight;
};

/**
 * Places the aside container into the DOM with the figure and its siblings.
 *
 * Wraps the figure element and its collected siblings inside the aside container,
 * positioning it to the left or right based on the specified alignment.
 *
 * Ensures proper spacing by cleaning up margins during placement.
 *
 * @param asideElement - The aside element to be positioned.
 * @param position - The desired float position.
 * @param siblings - The list of sibling elements to include in the container.
 */
export const placeAsideContainer = (
  asideElement: HTMLElement,
  position: 'right' | 'left',
  siblings: HTMLElement[],
): void => {
  const asideContainer = document.createElement('div');
  asideContainer.className = `bv-grid-float bv-grid-float--${position}`;
  asideContainer.classList.add('bo-article-aside');

  // Insert the container in the DOM and move the figure and siblings inside it
  siblings[0]?.insertAdjacentElement('beforebegin', asideContainer);
  asideContainer.appendChild(asideElement);
  asideElement.style.marginTop = ' 0'; // clean up aside element top margin
  siblings.forEach((sibling, idx) => {
    if (idx === 0) sibling.style.marginTop = ' 0'; // clean up first sibling top margin
    asideContainer.appendChild(sibling);
  });
};

/**
 * Checks if the figure and its previous sibling are both aside images.
 * Validates that both elements have the required classes for aside images.
 *
 * @param figure - The figure element to check.
 */
export const isConsecutiveAsideImage = (figure: HTMLElement): boolean => {
  const asideImageClasses = ['bo-image', 'bv-grid-float__item'];

  return (
    asideImageClasses.every(className =>
      figure.classList.contains(className),
    ) &&
    asideImageClasses.every(className =>
      figure.previousElementSibling?.classList.contains(className),
    )
  );
};
